/**
 * Particle system.
 *
 * Fixed-capacity pool, zero allocation in the hot loop. Dead particles are removed by
 * swapping with the last live element and shrinking the live count — no `splice`, no
 * garbage, no per-frame array churn. At 1400 particles this stays well under a
 * millisecond per frame.
 *
 * Randomness comes from an internal seeded PRNG rather than `Math.random`, so a replay of
 * the same match produces the same fireworks. It never touches the engine's PRNG.
 */

import type { PlayerId } from '../game/types';
import { INK, seatColor, withAlpha } from './palette';

export type ParticleKind =
  | 'spark' | 'ring' | 'dust' | 'ember' | 'shard' | 'glyph' | 'shockwave' | 'trail';

export interface EmitOptions {
  x: number;
  y: number;
  kind: ParticleKind;
  color?: string;
  count?: number;
  speed?: number;
  size?: number;
  life?: number;
  text?: string;
  seat?: PlayerId;
  angle?: number;
  spread?: number;
  gravity?: number;
}

/** Pool capacity. Sized so a four-way contest reveal cannot exhaust it. */
const CAPACITY = 1400;

/** Longest frame step we integrate; anything larger is a tab-switch, not a frame. */
const MAX_DT = 50;

export class ParticleSystem {
  // Struct-of-arrays: one typed array per attribute keeps the update loop cache-friendly
  // and means "allocating" a particle is just writing a few slots.
  private readonly px = new Float32Array(CAPACITY);
  private readonly py = new Float32Array(CAPACITY);
  private readonly vx = new Float32Array(CAPACITY);
  private readonly vy = new Float32Array(CAPACITY);
  private readonly size = new Float32Array(CAPACITY);
  private readonly age = new Float32Array(CAPACITY);
  private readonly life = new Float32Array(CAPACITY);
  private readonly gravity = new Float32Array(CAPACITY);
  private readonly spin = new Float32Array(CAPACITY);
  private readonly rot = new Float32Array(CAPACITY);
  private readonly kind: ParticleKind[] = new Array(CAPACITY).fill('spark');
  private readonly color: string[] = new Array(CAPACITY).fill('#ffffff');
  private readonly label: (string | null)[] = new Array(CAPACITY).fill(null);

  /** Number of live particles; everything at index >= this is free. */
  private live = 0;

  private rngState = 0x9e3779b9;

  get count(): number {
    return this.live;
  }

  private rand(): number {
    this.rngState = (this.rngState + 0x6d2b79f5) | 0;
    let t = this.rngState;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Claim a slot, or -1 when the pool is full (oldest-wins is not worth the bookkeeping). */
  private alloc(): number {
    if (this.live >= CAPACITY) return -1;
    return this.live++;
  }

  clear(): void {
    this.live = 0;
  }

  /* --------------------------------------------------------------------- emit */

  emit(opts: EmitOptions): void {
    const {
      x, y, kind, count = 1, speed = 0.14, size = 3, life = 700,
      angle, spread = Math.PI * 2, gravity = 0, text, seat,
    } = opts;
    const color = opts.color ?? (seat !== undefined ? seatColor(seat) : INK.text);

    for (let i = 0; i < count; i++) {
      const slot = this.alloc();
      if (slot < 0) return;

      const dir = angle !== undefined
        ? angle + (this.rand() - 0.5) * spread
        : this.rand() * Math.PI * 2;
      // Square-root distribution gives an even areal spread rather than a dense centre.
      const magnitude = speed * (0.35 + Math.sqrt(this.rand()) * 0.75);

      this.px[slot] = x;
      this.py[slot] = y;
      this.vx[slot] = Math.cos(dir) * magnitude;
      this.vy[slot] = Math.sin(dir) * magnitude;
      this.size[slot] = size * (0.6 + this.rand() * 0.8);
      this.age[slot] = 0;
      this.life[slot] = life * (0.75 + this.rand() * 0.5);
      this.gravity[slot] = gravity;
      this.rot[slot] = this.rand() * Math.PI * 2;
      this.spin[slot] = (this.rand() - 0.5) * 0.012;
      this.kind[slot] = kind;
      this.color[slot] = color;
      this.label[slot] = text ?? null;

      // Rings, shockwaves and floating text do not travel; they scale or drift.
      if (kind === 'ring' || kind === 'shockwave') {
        this.vx[slot] = 0;
        this.vy[slot] = 0;
      }
      if (kind === 'glyph') {
        this.vx[slot] = (this.rand() - 0.5) * 0.02;
        this.vy[slot] = -0.055 - this.rand() * 0.02;
      }
    }
  }

  /* ------------------------------------------------------- convenience bursts */

  burstClaim(x: number, y: number, seat: PlayerId): void {
    const color = seatColor(seat);
    this.emit({ x, y, kind: 'shockwave', color, count: 1, size: 16, life: 620 });
    this.emit({ x, y, kind: 'spark', color, count: 26, speed: 0.24, size: 2.6, life: 760 });
    this.emit({ x, y, kind: 'dust', color: withAlpha(color, 0.6), count: 14, speed: 0.08, size: 8, life: 1100 });
  }

  burstUpgrade(x: number, y: number, seat: PlayerId): void {
    const color = seatColor(seat);
    this.emit({ x, y, kind: 'ring', color, count: 2, size: 12, life: 780 });
    // Upward bias: a colony "rising" reads better than a symmetric pop.
    this.emit({
      x, y, kind: 'spark', color, count: 30, speed: 0.2, size: 2.4, life: 900,
      angle: -Math.PI / 2, spread: Math.PI * 1.1, gravity: 0.00012,
    });
    this.emit({ x, y, kind: 'ember', color: '#fff3c4', count: 12, speed: 0.1, size: 2, life: 1300 });
  }

  burstRelic(x: number, y: number): void {
    this.emit({ x, y, kind: 'shockwave', color: '#ffd76b', count: 1, size: 20, life: 900 });
    this.emit({ x, y, kind: 'shard', color: '#ffd76b', count: 18, speed: 0.18, size: 4, life: 1100 });
    this.emit({ x, y, kind: 'ember', color: '#fff0b8', count: 22, speed: 0.06, size: 2.2, life: 1600 });
  }

  burstContest(x: number, y: number): void {
    this.emit({ x, y, kind: 'shockwave', color: INK.bad, count: 2, size: 24, life: 560 });
    this.emit({ x, y, kind: 'shard', color: '#ff9a7a', count: 24, speed: 0.32, size: 3.4, life: 640 });
    this.emit({ x, y, kind: 'ember', color: '#ffd0a0', count: 16, speed: 0.12, size: 2, life: 1000, gravity: 0.00008 });
  }

  burstProduction(x: number, y: number, color: string): void {
    this.emit({
      x, y, kind: 'ember', color, count: 8, speed: 0.05, size: 2.4, life: 900,
      angle: -Math.PI / 2, spread: 1.1,
    });
  }

  floatText(x: number, y: number, text: string, color: string): void {
    this.emit({ x, y, kind: 'glyph', color, count: 1, size: 17, life: 1250, text });
  }

  /** A dotted streak between two hexes, for a fleet jump. */
  jumpTrail(fromX: number, fromY: number, toX: number, toY: number, seat: PlayerId): void {
    const color = seatColor(seat);
    const dx = toX - fromX;
    const dy = toY - fromY;
    const distance = Math.hypot(dx, dy) || 1;
    const steps = Math.min(28, Math.max(6, Math.round(distance / 14)));
    const angle = Math.atan2(dy, dx);

    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      this.emit({
        x: fromX + dx * t,
        y: fromY + dy * t,
        kind: 'trail',
        color,
        count: 1,
        // Trailing particles drift backwards along the path, so the streak reads directional.
        angle: angle + Math.PI,
        spread: 0.7,
        speed: 0.03,
        size: 3.2 * (1 - t * 0.55),
        // Later points live longer, so the head of the trail lingers at the destination.
        life: 320 + t * 420,
      });
    }
    this.emit({ x: toX, y: toY, kind: 'ring', color, count: 1, size: 9, life: 520 });
  }

  /* ------------------------------------------------------------------- update */

  update(dtMs: number): void {
    const dt = Math.min(dtMs, MAX_DT);

    for (let i = 0; i < this.live; i++) {
      this.age[i] += dt;

      if (this.age[i] >= this.life[i]) {
        // Swap with the last live particle and shrink; re-test this index next iteration.
        this.live--;
        if (i !== this.live) this.swap(i, this.live);
        i--;
        continue;
      }

      this.vy[i] += this.gravity[i] * dt;
      this.px[i] += this.vx[i] * dt;
      this.py[i] += this.vy[i] * dt;
      this.rot[i] += this.spin[i] * dt;

      // Air drag, frame-rate independent.
      const drag = Math.exp(-dt / 420);
      this.vx[i] *= drag;
      this.vy[i] *= drag;
    }
  }

  private swap(a: number, b: number): void {
    const swapF = (arr: Float32Array) => {
      const tmp = arr[a]!;
      arr[a] = arr[b]!;
      arr[b] = tmp;
    };
    swapF(this.px); swapF(this.py); swapF(this.vx); swapF(this.vy);
    swapF(this.size); swapF(this.age); swapF(this.life);
    swapF(this.gravity); swapF(this.spin); swapF(this.rot);

    const k = this.kind[a]!; this.kind[a] = this.kind[b]!; this.kind[b] = k;
    const c = this.color[a]!; this.color[a] = this.color[b]!; this.color[b] = c;
    const l = this.label[a]!; this.label[a] = this.label[b]!; this.label[b] = l;
  }

  /* --------------------------------------------------------------------- draw */

  draw(ctx: CanvasRenderingContext2D): void {
    if (this.live === 0) return;

    // Two passes: additive for light-emitting kinds, normal for text and shards. Batching
    // by blend mode means two composite-mode changes per frame instead of one per particle.
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    for (let i = 0; i < this.live; i++) {
      const kind = this.kind[i]!;
      if (kind === 'glyph' || kind === 'shard') continue;
      this.drawOne(ctx, i, kind);
    }
    ctx.restore();

    ctx.save();
    for (let i = 0; i < this.live; i++) {
      const kind = this.kind[i]!;
      if (kind !== 'glyph' && kind !== 'shard') continue;
      this.drawOne(ctx, i, kind);
    }
    ctx.restore();
  }

  private drawOne(ctx: CanvasRenderingContext2D, i: number, kind: ParticleKind): void {
    const t = this.age[i]! / this.life[i]!;
    const fade = 1 - t;
    const x = this.px[i]!;
    const y = this.py[i]!;
    const s = this.size[i]!;
    const color = this.color[i]!;

    switch (kind) {
      case 'spark':
      case 'ember':
      case 'dust':
      case 'trail': {
        // Cheap round glow via a two-stop radial gradient would cost an object per
        // particle, so use a solid disc with alpha instead — visually equivalent when
        // additively blended.
        const alpha = kind === 'dust' ? fade * 0.4 : fade * 0.95;
        const radius = kind === 'dust' ? s * (1 + t * 1.6) : s * (1 - t * 0.35);
        ctx.globalAlpha = Math.max(0, alpha);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(x, y, Math.max(0.2, radius), 0, Math.PI * 2);
        ctx.fill();
        break;
      }

      case 'ring': {
        ctx.globalAlpha = Math.max(0, fade * 0.85);
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(0.6, s * 0.28 * fade);
        ctx.beginPath();
        ctx.arc(x, y, s * (0.4 + t * 2.6), 0, Math.PI * 2);
        ctx.stroke();
        break;
      }

      case 'shockwave': {
        // Expands fast then thins — the classic impact read.
        const eased = 1 - (1 - t) ** 3;
        ctx.globalAlpha = Math.max(0, (1 - eased) * 0.9);
        ctx.strokeStyle = color;
        ctx.lineWidth = Math.max(0.5, s * 0.5 * (1 - eased));
        ctx.beginPath();
        ctx.arc(x, y, s * (0.3 + eased * 4.2), 0, Math.PI * 2);
        ctx.stroke();
        break;
      }

      case 'shard': {
        ctx.globalAlpha = Math.max(0, fade);
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(this.rot[i]!);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(0, -s);
        ctx.lineTo(s * 0.42, s * 0.7);
        ctx.lineTo(-s * 0.42, s * 0.7);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
        break;
      }

      case 'glyph': {
        const label = this.label[i];
        if (!label) break;
        // Hold at full opacity, then fade over the last 40% of life.
        ctx.globalAlpha = Math.max(0, Math.min(1, (1 - t) / 0.4));
        ctx.font = `800 ${s}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineWidth = Math.max(2, s * 0.22);
        ctx.strokeStyle = 'rgba(0,0,0,0.8)';
        ctx.strokeText(label, x, y);
        ctx.fillStyle = color;
        ctx.fillText(label, x, y);
        break;
      }
    }
  }
}
